import { isOpportunityId } from '../opportunity';
import type { ListField } from './keys';
import type { AccountState, IntentOp, IntentState } from './types';

/**
 * The device's three lists and the account's one intent per event.
 *
 * On the device, My Schedule, Save for Later and Not Interested are separate
 * lists. In the account each event has at most one state, ranked
 * going > saved > dismissed (D6). This file turns list writes into the batch
 * ops that `POST /intents/me/batch` takes, and the account's intents back into
 * lists.
 *
 * A write is read as what the user did to that list — these ids added, those
 * removed — compared with what the same hook last showed, never with the
 * account's latest copy. That keeps an older or partial view from ever
 * removing something it never showed, and it is what lets undo, redo and clear
 * keep working unchanged: each is just another before and after.
 */

export const STATE_OF_LIST: Record<ListField, IntentState> = {
  schedule: 'going',
  saved: 'saved',
  hidden: 'dismissed',
};

/** Account intents in a stable order: the server's, then each new event at the end, as the lists grow. */
export type Intents = Map<string, IntentState>;

export function intentsOf(state: AccountState): Intents {
  const out: Intents = new Map();
  // Listed lowest first, so a server that ever sent one id twice resolves upward.
  for (const id of state.intents.dismissed) out.set(id, 'dismissed');
  for (const id of state.intents.saved) out.set(id, 'saved');
  for (const id of state.intents.going) out.set(id, 'going');
  return out;
}

export function listOf(intents: Intents, list: ListField): string[] {
  const wanted = STATE_OF_LIST[list];
  const out: string[] = [];
  for (const [id, state] of intents) if (state === wanted) out.push(id);
  return out;
}

export interface ListChange {
  id: string;
  list: ListField;
  change: 'add' | 'remove';
}

/** What one list's write says the user did. Removals come first, as a move out of one list reads. */
export function listChanges(list: ListField, prev: readonly string[], next: readonly string[]): ListChange[] {
  const before = new Set(prev);
  const after = new Set(next);
  const out: ListChange[] = [];
  for (const id of before) if (!after.has(id)) out.push({ id, list, change: 'remove' });
  for (const id of after) if (!before.has(id)) out.push({ id, list, change: 'add' });
  return out;
}

/**
 * Applies list changes to the account's intents.
 *
 *   add to My Schedule      → going, from any state: it's an explicit action
 *   add to Save for Later   → saved, even from going (the buttons are toggles)
 *   add to Not Interested   → dismissed, only for an event with no state yet;
 *                             going and saved outrank it (D6)
 *   remove from a list      → cleared, only while that list's state is the
 *                             event's state; a move already made elsewhere wins
 *
 * The last rule is why the order of the schedule and saved writes doesn't
 * matter: a move from the schedule to Save for Later reads the same either way.
 */
export function resolveIntents(current: Intents, changes: readonly ListChange[]): { intents: Intents; ops: IntentOp[] } {
  const intents: Intents = new Map(current);
  const touched = new Set<string>();

  for (const { id, list, change } of changes) {
    const state = STATE_OF_LIST[list];
    const now = intents.get(id);
    if (change === 'add') {
      if (list === 'hidden' && now) continue;
      if (now !== state) {
        // A fresh add goes to the end, as it would on the device.
        intents.delete(id);
        intents.set(id, state);
        touched.add(id);
      }
    } else if (now === state) {
      intents.delete(id);
      touched.add(id);
    }
  }

  const ops: IntentOp[] = [];
  for (const id of touched) {
    const state = intents.get(id) ?? null;
    if ((current.get(id) ?? null) === state) continue;
    // Only backend ids can hold an intent; anything else would fail the whole batch.
    if (!isOpportunityId(id)) continue;
    ops.push({ opportunityId: id, state });
  }
  return { intents, ops };
}

export interface ListsSnapshot {
  schedule?: readonly string[];
  saved?: readonly string[];
  hidden?: readonly string[];
}

/**
 * The batch ops that take the account from one view of the lists to another,
 * for whichever lists the two snapshots name.
 */
export function diffLists(
  current: Intents,
  prev: ListsSnapshot,
  next: ListsSnapshot,
): { intents: Intents; ops: IntentOp[] } {
  const changes: ListChange[] = [];
  for (const list of ['schedule', 'saved', 'hidden'] as const) {
    const before = prev[list];
    const after = next[list];
    if (before && after) changes.push(...listChanges(list, before, after));
  }
  return resolveIntents(current, changes);
}
